/*
 * The OIDC client. Framework-free, so it can be tested without a renderer.
 *
 * Authorization code with PKCE, per RFC 7636. The SPA is a public client: anything compiled into
 * it is readable by anyone who opens devtools, so there is no client secret and the proof that
 * the code belongs to this session is a verifier the browser generated and never transmitted
 * until the exchange.
 *
 * Endpoints come from the provider's discovery document rather than from configuration. That is
 * what makes the WorkOS migration a change of one environment variable: the fake and the real
 * provider publish the same document, so the app never learns which it is talking to.
 */

import { config } from '../config.js';

export interface Discovery {
  readonly authorization_endpoint: string;
  readonly token_endpoint: string;
  readonly end_session_endpoint?: string;
  readonly code_challenge_methods_supported?: readonly string[];
}

export interface TokenSet {
  readonly accessToken: string;
  /** Absolute epoch milliseconds. Stored as an instant, not a duration, so a reload can use it. */
  readonly expiresAt: number;
  /**
   * When this session began — the moment the code was exchanged for a token.
   *
   * Recorded here rather than read from the token's `iat`, though the access token carries one.
   * Decoding a JWT in the browser to display a timestamp means writing a decoder, and a decoder
   * invites treating the payload's *other* claims as trustworthy — which they are not without
   * verifying the signature, and verification is the API's job. This value makes a narrower and
   * honest claim: when this tab acquired its session. It survives a reload because it lives in the
   * same sessionStorage entry as the token, and it dies with the tab, which is correct — a new tab
   * is a new session as far as anybody looking at the header is concerned.
   */
  readonly signedInAt: number;
}

/**
 * Where the in-flight request is kept between the redirect out and the redirect back.
 *
 * sessionStorage, not localStorage: the verifier is a single-use secret for one login attempt in
 * one tab. localStorage would share it across tabs and outlive the attempt, which is a longer
 * life than a one-time secret should have.
 */
const PENDING_KEY = 'tf.auth.pending';

interface PendingLogin {
  readonly state: string;
  readonly verifier: string;
  readonly returnTo: string;
  /**
   * Which provider this attempt went to.
   *
   * Persisted with the verifier rather than recomputed on the way back, because the callback URL
   * says nothing about where the code came from — both providers redirect to the same
   * `/auth/callback`. Getting this wrong means exchanging a code at the wrong token endpoint,
   * which fails as an opaque 400 well after the interesting part is over.
   *
   * Absent on a record written before this field existed, which reads as `primary` — the only
   * provider that could have written it.
   */
  readonly provider?: ProviderKey;
}

/** Which of the configured providers a sign-in is running against. */
export type ProviderKey = 'primary' | 'alt';

/** Its issuer and client id. Throws for `alt` when none is configured, which is a caller bug. */
function providerFor(key: ProviderKey): {
  issuer: string;
  clientId: string;
  authorizeParams?: Readonly<Record<string, string>>;
} {
  if (key === 'alt') {
    if (!config.altOidc) {
      throw new Error('No second identity provider is configured for this build.');
    }
    return {
      issuer: config.altOidc.issuer,
      clientId: config.altOidc.clientId,
      authorizeParams: config.altOidc.authorizeParams,
    };
  }
  return {
    issuer: config.oidcIssuer,
    clientId: config.oidcClientId,
    authorizeParams: config.oidcAuthorizeParams,
  };
}

const base64url = (bytes: ArrayBuffer): string => {
  const binary = String.fromCharCode(...new Uint8Array(bytes));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64url(bytes.buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(digest);
}

/**
 * One cached document per provider — a map since 19 Aug 2026, when a build could have two.
 *
 * Keyed by issuer rather than by `ProviderKey` so the cache cannot outlive a reconfiguration in
 * the confusing direction: a changed issuer is a different entry, never a stale hit.
 */
const discoveryPromises = new Map<string, Promise<Discovery>>();

export async function discover(provider: ProviderKey = 'primary'): Promise<Discovery> {
  const { issuer } = providerFor(provider);

  // Cached for the life of the page. The document is effectively static, and re-fetching it on
  // every token refresh would add a round trip to a path that is already on the critical way in.
  const cached = discoveryPromises.get(issuer);
  if (cached) {
    return cached;
  }

  const pending = fetch(`${issuer}/.well-known/openid-configuration`).then(async (response) => {
    if (!response.ok) {
      throw new Error(`The identity provider's discovery document is unavailable (${response.status}).`);
    }
    return (await response.json()) as Discovery;
  });

  discoveryPromises.set(issuer, pending);
  return pending;
}

/**
 * Sends the browser to the provider. Does not return — the page navigates away.
 *
 * `extraParams` is per attempt, merged over the provider's configured ones. It exists for the
 * invitation flow: a welcome email carries `invitation_token`, and handing that to the
 * authorization request is what makes the provider accept the invitation *inside* a flow that
 * keeps our `redirect_uri` — so the person lands back here signed in, rather than on the
 * provider's own placeholder page.
 *
 * Merged over rather than under the configured ones, because a value chosen for this attempt is
 * more specific than one chosen for the build.
 */
export async function beginSignIn(
  returnTo: string,
  provider: ProviderKey = 'primary',
  extraParams: Readonly<Record<string, string | undefined>> = {},
): Promise<void> {
  const { authorization_endpoint } = await discover(provider);
  const { clientId, authorizeParams } = providerFor(provider);

  const state = randomString();
  const verifier = randomString();
  const pending: PendingLogin = { state, verifier, returnTo, provider };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));

  const url = new URL(authorization_endpoint);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', config.oidcRedirectUri);
  /*
   * `offline_access`, added 01 Oct 2026 — and its absence is why people were being logged out
   * while working.
   *
   * The scope was `openid email`, so the provider issued an access token and **no refresh token at
   * all**. There was therefore nothing to renew with: when the access token reached its life the
   * next call answered 401, and the only honest thing left was to send somebody back to sign in —
   * mid-edit, on a product they were plainly still using.
   *
   * The password door never had this problem because it mints a session of our own with an
   * httpOnly refresh cookie. This is the provider door's equivalent, and it is the scope that has
   * to be asked for at the start: a refresh token cannot be obtained later from a session that was
   * never granted one.
   */
  url.searchParams.set('scope', 'openid email offline_access');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', await challengeFor(verifier));
  url.searchParams.set('code_challenge_method', 'S256');

  /*
   * Whatever else this provider needs, set last and by name only.
   *
   * WorkOS refuses a bare request with `invalid-connection-selector` — the standard parameters do
   * not say which flow is meant, and it will not guess. This client does not know that; it is
   * handed `provider=authkit` as configuration. Set after the standard parameters so a
   * misconfiguration cannot quietly overwrite `code_challenge` or `state`... which it still could,
   * so it is worth saying plainly: these are trusted values from the build's own environment, in
   * the same category as the client id beside them, and not anything a visitor can reach.
   */
  for (const [key, value] of Object.entries({ ...authorizeParams, ...extraParams })) {
    if (value !== undefined && value !== '') {
      url.searchParams.set(key, value);
    }
  }

  window.location.assign(url.toString());
}

export class SignInError extends Error {
  readonly reason: 'state' | 'missing_code' | 'exchange' | 'provider' | 'blocked';

  constructor(reason: SignInError['reason'], message: string) {
    super(message);
    this.name = 'SignInError';
    this.reason = reason;
  }
}

export interface CallbackResult {
  readonly tokens: TokenSet;
  readonly returnTo: string;
}

/**
 * Completes the flow from the callback URL's query string.
 *
 * The state check is the one step that cannot be skipped for convenience: without it, a code
 * injected by another page would be exchanged by this app and would produce a session belonging
 * to someone else. It is compared against the value stashed before the redirect, and the stash
 * is cleared whatever the outcome so a failed attempt cannot be retried with a stale verifier.
 */
export async function completeSignIn(search: URLSearchParams): Promise<CallbackResult> {
  const raw = sessionStorage.getItem(PENDING_KEY);
  sessionStorage.removeItem(PENDING_KEY);

  const providerError = search.get('error');
  if (providerError) {
    throw new SignInError(
      'provider',
      search.get('error_description') ?? providerError,
    );
  }

  const pending: PendingLogin | undefined = raw ? (JSON.parse(raw) as PendingLogin) : undefined;
  if (!pending || pending.state !== search.get('state')) {
    throw new SignInError('state', 'The sign-in response did not match the request that started it.');
  }

  const code = search.get('code');
  if (!code) {
    throw new SignInError('missing_code', 'The identity provider returned no authorization code.');
  }

  // The provider this attempt actually left from, not whichever is configured first. Both
  // providers land on the same callback route, so the stash is the only record of which door
  // this code came through.
  const provider = pending.provider ?? 'primary';
  const { token_endpoint } = await discover(provider);
  const { clientId } = providerFor(provider);

  /*
   * A `TypeError` here means the exchange failed and the browser will not say how — 19 Aug 2026.
   *
   * `fetch` rejects with `TypeError: Failed to fetch` when the page is not allowed to read the
   * response. At this point nothing else produces one: the URL came from the provider's own
   * discovery document, so it is neither malformed nor unreachable.
   *
   * **This was first read as "the origin is not on the allow list", and that was too narrow.**
   * Measured against WorkOS once the allow list did name the origin: the preflight echoes it, and
   * a 200 carries `access-control-allow-origin` — but a 4xx from the token endpoint carries no
   * CORS header at all. Their error path returns before the middleware that would add it.
   *
   * So this branch is reached by *every* failed exchange, not by a misconfigured allow list, and
   * the previous message sent an operator to fix something that was already correct. What can be
   * proved here is only that the attempt was refused and the reason is unreadable — so the message
   * says that, and points at the one place the reason survives: the network tab, which records the
   * response the script was forbidden to see.
   */
  /*
   * Same-origin when a diagnostic proxy is configured — see `config.oidcErrorProxy`.
   *
   * The path is preserved and only the origin moves, so the dev server forwards it unchanged and
   * the provider sees exactly the request it would have seen from the browser. What changes is
   * only who is allowed to read the answer.
   *
   * The provider names its own mount — 20 Aug 2026. Without it the prefix was shared by both
   * providers while the dev server could only forward to one, so with WorkOS configured a code
   * from the local provider was presented to WorkOS and came back a 404. `provider` is already
   * the door this attempt left from, which is exactly what the mount has to agree with.
   */
  const endpoint = config.oidcErrorProxy
    ? `${window.location.origin}${config.oidcErrorProxy}/${provider}${new URL(token_endpoint).pathname}`
    : token_endpoint;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      code_verifier: pending.verifier,
      redirect_uri: config.oidcRedirectUri,
      client_id: clientId,
    }),
  }).catch((cause: unknown) => {
    if (cause instanceof TypeError) {
      throw new SignInError(
        'blocked',
        `The identity provider would not answer a request from ${window.location.origin}. ` +
          'Its CORS allow list has to name this origin — until it does, the browser cannot read ' +
          'the response and no further detail is available here.',
      );
    }
    throw cause;
  });

  if (!response.ok) {
    /*
     * The provider's own words, when they are readable — which is only through the proxy above.
     *
     * Bounded and swallowed on failure for the same reason the WorkOS adapter bounds its error
     * body: an error path that can itself throw turns a diagnosable problem into an unhandled
     * rejection. Cross-origin this is unreachable, because a failed exchange never gets past the
     * `TypeError` branch.
     */
    const detail = await response
      .text()
      .then((body) => body.slice(0, 300).replace(/\s+/g, ' ').trim())
      .catch(() => '');

    throw new SignInError(
      'exchange',
      `The authorization code could not be exchanged (${response.status})` +
        (detail ? `: ${detail}` : '.'),
    );
  }

  const body = (await response.json()) as {
    access_token: string;
    expires_in?: number;
    refresh_token?: string;
  };

  /*
   * The refresh token is kept **in memory only** and never returned to the caller.
   *
   * `session.tsx`'s own header makes the argument for the access token sitting in `sessionStorage`
   * — one tab, cleared on close, survives a reload. A refresh token is a different kind of secret:
   * it is the long-lived one, and the whole reason the password door puts its equivalent in an
   * httpOnly cookie is that script must not be able to read it. Keeping it in a module variable is
   * the closest this door can get while the exchange happens in the browser.
   *
   * The cost is honest and stated: a full page reload loses it, and that reload lands on the
   * provider for a fresh token. Closing that gap means either an httpOnly cookie minted by our own
   * API or `prompt=none` re-authentication, and both are a slice rather than a line.
   */
  if (body.refresh_token) {
    refreshToken = body.refresh_token;
    refreshProvider = provider;
  }

  return {
    tokens: {
      accessToken: body.access_token,
      // A minute of slack, so a request started just before expiry does not arrive just after.
      expiresAt: Date.now() + ((body.expires_in ?? 3600) - 60) * 1000,
      signedInAt: Date.now(),
    },
    returnTo: pending.returnTo,
  };
}

/* The provider's refresh token, and which door it came from. Memory only — see above. */
let refreshToken: string | null = null;
let refreshProvider: ProviderKey = 'primary';
let renewal: Promise<TokenSet | null> | null = null;

/** Whether this browser has anything to renew a provider session with. */
export function mayRenewWithProvider(): boolean {
  return refreshToken !== null;
}

export function forgetProviderRefresh(): void {
  refreshToken = null;
  renewal = null;
}

/**
 * Exchanges the refresh token for a new access token, at most once at a time.
 *
 * Single-flight for the reason the password door's renewal is: a screen opening six queries at once
 * would otherwise fire six exchanges, and a provider that rotates refresh tokens would have the
 * winner invalidate the other five — turning an ordinary expiry into the sign-out this exists to
 * prevent.
 *
 * Never throws. A provider that refuses is a session that is genuinely over, and the caller's next
 * move is the same either way: send what it has and let the server decide.
 */
export async function renewWithProvider(): Promise<TokenSet | null> {
  if (!refreshToken) return null;

  if (!renewal) {
    const started = exchangeRefreshToken().catch(() => null);
    renewal = started.finally(() => {
      renewal = null;
    });
  }

  return renewal;
}

async function exchangeRefreshToken(): Promise<TokenSet | null> {
  const current = refreshToken;
  if (!current) return null;

  const { token_endpoint } = await discover(refreshProvider);
  const { clientId } = providerFor(refreshProvider);

  const endpoint = config.oidcErrorProxy
    ? `${window.location.origin}${config.oidcErrorProxy}/${refreshProvider}${new URL(token_endpoint).pathname}`
    : token_endpoint;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: current,
      client_id: clientId,
    }),
  });

  if (!response.ok) {
    // Refused means the session is over at the provider. Forgetting it stops every later call
    // retrying a credential that has already been told no.
    forgetProviderRefresh();
    return null;
  }

  const body = (await response.json()) as {
    access_token: string;
    expires_in?: number;
    refresh_token?: string;
  };

  // Providers that rotate hand back a new one; those that do not return nothing and the old one
  // stays valid. Both are normal, and assuming either would break the other.
  if (body.refresh_token) refreshToken = body.refresh_token;

  const tokens: TokenSet = {
    accessToken: body.access_token,
    expiresAt: Date.now() + ((body.expires_in ?? 3600) - 60) * 1000,
    signedInAt: Date.now(),
  };

  /*
   * Returned rather than stored here. `storeTokens` belongs to `session.tsx`, which already imports
   * this file — reaching back for it would be a cycle, and the caller is the thing that knows
   * whether this token is the one it is about to use.
   */
  return tokens;
}

export async function endSessionUrl(provider: ProviderKey = 'primary'): Promise<string | undefined> {
  const { end_session_endpoint } = await discover(provider);
  if (!end_session_endpoint) {
    return undefined;
  }
  const url = new URL(end_session_endpoint);
  url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}/sign-in`);
  return url.toString();
}
